import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcFilterLine } from "components/filter";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { UserInfoDto, UserOperationType } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBroadcastMessageContext } from "views/foundation/broadcastMessage/Home/StepManagement";
import { BatchEditSelectedUsers } from "views/foundation/user/components/BatchEdit/BatchEditSelectedUsers";
import { OPERATION_PAGE_SIZE } from "views/lms/enrollment/Home";

////////////////////////////////////////////////////////////////////////////

interface IProps {
  reload: boolean;
  onFinishReload: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const SelectedUsersBox: React.FC<IProps> = ({ reload, onFinishReload }) => {

  const ctx = useBroadcastMessageContext();
  const { t } = useTranslation();
  const api = useCoreApi();

  const [viewError, setViewError] = useState<any>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);

  ///
  const handleDelete = (item: UserInfoDto) => {
    setSaving(true);
    api.v1.userOperation.removeUser({
      operation: UserOperationType.BroadcastMessage,
      userIds: [item.userId]
    }).then(result => {
      if (result.data) getData();
      else enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
    }).catch(e => {
      MprcRequestSnackbarErrorReport(e);
    }).finally(() => setSaving(false));
  };

  ///
  const clearAll = () => {
    setSaving(true);
    api.v1.userOperation.clearUsers({
      operation: UserOperationType.BroadcastMessage
    }).then(result => {
      if (result.data) getData(1);
      else enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
    }).catch(e => {
      MprcRequestSnackbarErrorReport(e);
    }).finally(() => setSaving(false));
  };

  ///
  const getData = (pg?: number) => {
    setLoading(true);
    api.v1.userOperation.getSelectedUsers({
      operation: UserOperationType.BroadcastMessage,
      allActiveUsers: ctx.message.allActiveUsers,
      pageNumber: pg ?? pageNumber,
      pageSize: OPERATION_PAGE_SIZE
    })
      .then(response => {
        ctx.setSelectedUsers(response.data);
      })
      .catch(e => {
        ctx.setSelectedUsers(undefined);
        setViewError(e);
      })
      .finally(() => {
        setLoading(false);
        onFinishReload();
      });
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT'S
  ////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (reload) {
      getData(1);
    }
  }, [reload]);

  useEffect(() => {
    getData(1);
  }, [ctx.message.allActiveUsers]);

  useEffect(() => {
    getData(pageNumber);
  }, [pageNumber]);

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
      <MprcRequestIndicator visible={loading || saving} type={saving ? "post" : "request"} />

      <BatchEditSelectedUsers
        onClearAll={clearAll}
        onDeleteUser={handleDelete}
        canDelete={!ctx.message.allActiveUsers}
        onChangePage={page => setPageNumber(page)}
        onReload={() => setPageNumber(1)}
        customFilter={
          <MprcFilterLine>
            <MprcSwitch label="Todos os Usuários Ativos" value={ctx.message.allActiveUsers} onChange={e => {
              ctx.setMessage({ ...ctx.message, allActiveUsers: e.target.checked });
            }} />
          </MprcFilterLine>
        }
        viewData={ctx.selectedUsers}
      />
    </>
  );
};
